import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { Button, Dropdown } from "react-bootstrap";
import "./styles.scss";

const MOBILE_BREAKPOINT = 576;
const VIEWPORT_PADDING = 10;
const DROPDOWN_GAP = 4;

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" &&
            window.innerWidth < MOBILE_BREAKPOINT
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return isMobile;
}

function LanguageButtonWithDialect({
    currentLanguage,
    targetLanguage,
    onLangSelect,
}) {
    const isMobile = useIsMobile();

    const [show, setShow] = useState(false);
    const [mobilePosition, setMobilePosition] = useState(null);

    const toggleRef = useRef(null);
    const menuRef = useRef(null);

    const calculateMobilePosition = useCallback(() => {
        if (
            !isMobile ||
            !show ||
            !toggleRef.current ||
            !menuRef.current
        ) {
            return;
        }

        const toggleRect =
            toggleRef.current.getBoundingClientRect();

        const menuRect =
            menuRef.current.getBoundingClientRect();

        const viewportWidth = window.innerWidth;

        const dropdownWidth = menuRect.width;

        /*
         * First attempt:
         *
         * Right-align dropdown with the button.
         */
        let left =
            toggleRect.right - dropdownWidth;

        /*
         * If dropdown would go beyond the LEFT
         * side of the screen, move it right.
         */
        if (left < VIEWPORT_PADDING) {
            left = VIEWPORT_PADDING;
        }

        /*
         * If dropdown would go beyond the RIGHT
         * side of the screen, move it left.
         */
        const rightEdge =
            left + dropdownWidth;

        if (
            rightEdge >
            viewportWidth - VIEWPORT_PADDING
        ) {
            left =
                viewportWidth -
                dropdownWidth -
                VIEWPORT_PADDING;
        }

        /*
         * Final safety check.
         */
        left = Math.max(
            VIEWPORT_PADDING,
            Math.min(
                left,
                viewportWidth -
                    dropdownWidth -
                    VIEWPORT_PADDING
            )
        );

        /*
         * Position below the button.
         */
        const top =
            toggleRect.bottom + DROPDOWN_GAP;

        setMobilePosition({
            left,
            top,
        });
    }, [isMobile, show]);

    /*
     * Wait for the menu to actually render before
     * measuring its width.
     */
    useLayoutEffect(() => {
        if (!isMobile || !show) {
            setMobilePosition(null);
            return;
        }

        const timer = setTimeout(() => {
            calculateMobilePosition();
        }, 0);

        return () => {
            clearTimeout(timer);
        };
    }, [
        isMobile,
        show,
        calculateMobilePosition,
    ]);

    /*
     * Recalculate when the viewport changes.
     */
    useEffect(() => {
        if (!isMobile || !show) {
            return;
        }

        const handleViewportChange = () => {
            requestAnimationFrame(() => {
                calculateMobilePosition();
            });
        };

        window.addEventListener(
            "resize",
            handleViewportChange
        );

        window.addEventListener(
            "orientationchange",
            handleViewportChange
        );

        /*
         * Capture scrolling because the language
         * selector itself can be inside a scrolling area.
         */
        window.addEventListener(
            "scroll",
            handleViewportChange,
            true
        );

        return () => {
            window.removeEventListener(
                "resize",
                handleViewportChange
            );

            window.removeEventListener(
                "orientationchange",
                handleViewportChange
            );

            window.removeEventListener(
                "scroll",
                handleViewportChange,
                true
            );
        };
    }, [
        isMobile,
        show,
        calculateMobilePosition,
    ]);

    const handleDropdownToggle = nextShow => {
        setShow(nextShow);

        if (!nextShow) {
            setMobilePosition(null);
        }
    };

    /*
     * Mobile menu style.
     *
     * IMPORTANT:
     * Do NOT use inset here.
     */
    const mobileMenuStyle =
        isMobile && mobilePosition
            ? {
                  position: "fixed",
                  left: `${mobilePosition.left}px`,
                  top: `${mobilePosition.top-15}px`,
                  right: "auto",
                  bottom: "auto",
                  transform: "none",
                  margin: 0,
                  zIndex: 3000,
              }
            : undefined;

    return (
        <Dropdown
            show={show}
            onToggle={handleDropdownToggle}
            className={
                isMobile
                    ? "mobile-language-dropdown"
                    : ""
            }
        >
            <Dropdown.Toggle
                ref={toggleRef}
                variant={
                    currentLanguage.uid ===
                    targetLanguage?.uid
                        ? "primary"
                        : "outline-dark"
                }
                className="rounded-5 me-2 mt-3 dialect-select-dropdown-toggle"
            >
                {currentLanguage.langText} ❯
            </Dropdown.Toggle>

            <Dropdown.Menu
                ref={menuRef}
                className={`dialect-select-dropdown rounded-4 shadow-lg border-0 ${
                    isMobile
                        ? "mobile-dialect-menu"
                        : ""
                }`}
                style={mobileMenuStyle}
                popperConfig={
                    isMobile
                        ? {
                              strategy: "fixed",
                              modifiers: [
                                  {
                                      name: "flip",
                                      enabled: false,
                                  },
                                  {
                                      name: "preventOverflow",
                                      enabled: false,
                                  },
                                  {
                                      name: "offset",
                                      enabled: false,
                                  },
                                  {
                                      name: "computeStyles",
                                      options: {
                                          adaptive: false,
                                      },
                                  },
                              ],
                          }
                        : {
                              modifiers: [
                                  {
                                      name: "flip",
                                      options: {
                                          boundary:
                                              "viewport",
                                          padding: 10,
                                      },
                                  },
                                  {
                                      name: "preventOverflow",
                                      options: {
                                          boundary:
                                              "viewport",
                                          padding: 10,
                                          altAxis: true,
                                      },
                                  },
                              ],
                          }
                }
            >
                <div className="dialect-dropdown-content">
                    {currentLanguage.dialects.map(
                        dialect => (
                            <Dropdown.Item
                                key={
                                    dialect.dialectCode
                                }
                                onClick={() =>
                                    onLangSelect?.(
                                        currentLanguage,
                                        dialect
                                    )
                                }
                            >
                                {
                                    dialect.dialectName
                                }

                                {dialect.translatedDialectName
                                    ? ` - ${dialect.translatedDialectName}`
                                    : ""}
                            </Dropdown.Item>
                        )
                    )}
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
}

function LanguageButton({
    currentLanguage,
    targetLanguage,
    onLangSelect,
}) {
    return (
        <Button
            variant={
                currentLanguage.uid ===
                targetLanguage?.uid
                    ? "primary"
                    : "outline-dark"
            }
            className="rounded-5 me-2 mt-3 dialect-select-dropdown-toggle"
            onClick={() =>
                onLangSelect?.(
                    currentLanguage,
                    currentLanguage.dialects[0]
                )
            }
        >
            {currentLanguage.langText}
        </Button>
    );
}

function LanguageList({
    show,
    langList,
    targetLanguage,
    onLangSelect,
}) {
    return (
        <div
            className={`sliding-div-container ${
                show ? "show" : ""
            }`}
        >
            <div className="language-list-content d-flex flex-wrap justify-content-center">
                {langList
                    .filter(
                        language => !language.hidden
                    )
                    .map(language =>
                        language.dialects.length >
                        1 ? (
                            <LanguageButtonWithDialect
                                key={language.uid}
                                currentLanguage={
                                    language
                                }
                                targetLanguage={
                                    targetLanguage
                                }
                                onLangSelect={
                                    onLangSelect
                                }
                            />
                        ) : (
                            <LanguageButton
                                key={language.uid}
                                currentLanguage={
                                    language
                                }
                                targetLanguage={
                                    targetLanguage
                                }
                                onLangSelect={
                                    onLangSelect
                                }
                            />
                        )
                    )}
            </div>
        </div>
    );
}

export default LanguageList;